// League Table widget — standings for the filter bar's league + season
// (consumes [league, season]). Data from GET /league-table.

import { useLeagueTable } from "../data/leagues.js";
import type { WidgetScope } from "./scope.js";
import { theme } from "../lib/theme.js";

const num: React.CSSProperties = {
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  padding: "5px 6px",
  color: theme.ink,
};
const head: React.CSSProperties = {
  textAlign: "right",
  padding: "5px 6px",
  color: theme.inkMuted,
  fontWeight: 500,
  position: "sticky",
  top: 0,
  background: theme.tile,
};

export default function LeagueTableWidget({ scope }: { scope: WidgetScope }) {
  const { data, isPending, isError, error } = useLeagueTable(scope.competitionId, scope.seasonId);

  if (scope.competitionId === null || scope.seasonId === null)
    return <Msg>Pick a league and season above.</Msg>;
  if (isPending) return <Msg>Loading table…</Msg>;
  if (isError) return <Msg danger>Couldn't load the table. {String(error)}</Msg>;
  if (data.table.length === 0) return <Msg>No standings for this season yet.</Msg>;

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr>
          <th style={{ ...head, textAlign: "left", width: 24 }}>#</th>
          <th style={{ ...head, textAlign: "left" }}>Team</th>
          <th style={head}>P</th>
          <th style={head}>W</th>
          <th style={head}>D</th>
          <th style={head}>L</th>
          <th style={head}>GF</th>
          <th style={head}>GA</th>
          <th style={head}>GD</th>
          <th style={{ ...head, color: theme.accent }}>Pts</th>
        </tr>
      </thead>
      <tbody>
        {data.table.map((t, i) => (
          <tr key={t.name} style={{ borderTop: `1px solid ${theme.border}` }}>
            <td style={{ padding: "5px 6px", color: theme.inkFaint }}>{i + 1}</td>
            <td style={{ padding: "5px 6px", color: theme.ink }}>{t.name}</td>
            <td style={num}>{t.played}</td>
            <td style={num}>{t.won}</td>
            <td style={num}>{t.drawn}</td>
            <td style={num}>{t.lost}</td>
            <td style={num}>{t.goalsFor}</td>
            <td style={num}>{t.goalsAgainst}</td>
            <td style={num}>{t.goalDifference > 0 ? `+${t.goalDifference}` : t.goalDifference}</td>
            <td style={{ ...num, color: theme.accent, fontWeight: 600 }}>{t.points}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
